import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SnackbarContext } from './SnackbarContext';

export interface SnackBarOptions {
  onError?: React.ReactNode | string;
  onSuccess?: React.ReactNode | string;
}

export interface SnackbarHttp {
  onSuccess?: () => void;
  onError?: () => void;
}

export type SnackbarType = 'error' | 'success' | 'warning' | 'info';

export interface Snackbar {
  id: number;
  type: SnackbarType;
  reactNode: React.ReactNode;
  infinite?: boolean;
}

export interface SnackbarBase {
  type: SnackbarType;
  reactNode: React.ReactNode;
  infinite?: boolean;
}

interface SnackbarProviderProps {
  children: React.ReactNode;
}

const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbars, setSnackbars] = useState<Snackbar[]>([]);
  const snackbarsRef = useRef(snackbars);
  snackbarsRef.current = snackbars;

  const pushSnackbar = useCallback((snackbar: SnackbarBase) => {
    const id = new Date().valueOf();
    setSnackbars((s) => [
      ...s,
      {
        id,
        ...snackbar,
      },
    ]);
    return id;
  }, []);

  const pushSnackbarSuccess = (reactNode: React.ReactNode) =>
    pushSnackbar({ type: 'success', reactNode });

  const pushSnackbarError = (reactNode: React.ReactNode) =>
    pushSnackbar({ type: 'error', reactNode });

  const pushSnackbarWarning = (reactNode: React.ReactNode) =>
    pushSnackbar({ type: 'warning', reactNode });

  const pushSnackbarInfo = (reactNode: React.ReactNode, infinite?: boolean) =>
    pushSnackbar({ type: 'info', reactNode, infinite });

  const removeSnackbar = useCallback((id: number) => {
    const nextSnackbars = snackbarsRef.current.slice();
    const index = nextSnackbars.findIndex((x) => x.id === id);
    if (index > -1) {
      nextSnackbars.splice(index, 1);
      setSnackbars(nextSnackbars);
    }
  }, []);

  const updateSnackbar = useCallback((id: number, snackbar: SnackbarBase) => {
    const nextSnackbars = snackbarsRef.current.slice();
    const index = nextSnackbars.findIndex((x) => x.id === id);
    if (index > -1) {
      nextSnackbars[index] = {
        id,
        ...snackbar,
      };
      setSnackbars(nextSnackbars);
    }
  }, []);

  const value: SnackbarContext = useMemo(
    () => ({
      snackbars,
      pushSnackbar,
      removeSnackbar,
      updateSnackbar,
      pushSnackbarSuccess,
      pushSnackbarError,
      pushSnackbarWarning,
      pushSnackbarInfo,
    }),
    [snackbars],
  );

  return <SnackbarContext.Provider value={value}>{children}</SnackbarContext.Provider>;
};

export default SnackbarProvider;
